import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';

registerLocaleData(localeDe, 'de', localeDeExtra);

import { bootstrapApplication } from '@angular/platform-browser';
import { RootComponent } from './app/shells/root.component';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  TitleStrategy,
} from '@angular/router';
import { ROOT_ROUTES } from './app/shells/root.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  isDevMode,
  provideZonelessChangeDetection,
} from '@angular/core';
import { CustomTitleStrategy } from '@core/navigation';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { authInterceptor } from '@core/auth';
import { errorInterceptor } from '@core/error-handling';
import {
  ApplicationInsightsService,
  telemetryInterceptor,
  GlobalErrorHandler,
} from '@core/telemetry';
import { provideServiceWorker } from '@angular/service-worker';
import { AppUpdatesStore } from './app/features/app-updates';
import { ThemeStore } from '@core/theme';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { CustomDateAdapter } from '@core/theme';
import { cpoVersionInterceptor } from '@core/user-agent';
import { provideSignalFormsConfig, FormField } from '@angular/forms/signals';

/**
 * Signal Forms status classes configuration.
 * These classes are automatically added to form fields based on their state.
 * Used with global CSS to style mat-form-field error states via :has() selector.
 */
const SIGNAL_FORMS_STATUS_CLASSES: Record<
  string,
  (field: FormField<unknown>) => boolean
> = {
  'ng-touched': (field) => field.state().touched(),
  'ng-untouched': (field) => !field.state().touched(),
  'ng-dirty': (field) => field.state().dirty(),
  'ng-pristine': (field) => !field.state().dirty(),
  'ng-valid': (field) => field.state().valid(),
  'ng-invalid': (field) => field.state().invalid(),
};

bootstrapApplication(RootComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      ROOT_ROUTES,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      })
    ),
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
          timeInput: { hour: '2-digit', minute: '2-digit', hour12: false },
        },
        display: {
          dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
          monthYearLabel: { month: 'short', year: 'numeric' },
          dateA11yLabel: { day: '2-digit', month: '2-digit', year: 'numeric' },
          monthYearA11yLabel: { month: 'long', year: 'numeric' },
          timeInput: { hour: '2-digit', minute: '2-digit', hour12: false },
          timeOptionLabel: {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          },
        },
      },
    },
    provideHttpClient(
      withInterceptors([
        telemetryInterceptor,
        cpoVersionInterceptor,
        authInterceptor,
        errorInterceptor,
      ])
    ),
    importProvidersFrom(BrowserAnimationsModule),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWithDelay:5000',
    }),
    provideAppInitializer(() => {
      const appInsights = inject(ApplicationInsightsService);
      appInsights.initialize();
    }),
    provideAppInitializer(() => {
      inject(AppUpdatesStore);
      inject(ThemeStore);
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideSignalFormsConfig({
      classes: SIGNAL_FORMS_STATUS_CLASSES,
    }),
  ],
}).catch((err) => console.error('Bootstrap failed', err));
